import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Upload, Eye, EyeOff, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const BannerManagement = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    link_url: '',
    display_order: 0,
    gradient_from: 'orange-400',
    gradient_to: 'orange-500',
    emoji: '🎉',
    image: null
  });

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/banners?active_only=false`);
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Gagal memuat banner');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    // Create FormData for file upload
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('subtitle', formData.subtitle);
    formDataToSend.append('link_url', formData.link_url);
    formDataToSend.append('display_order', formData.display_order);
    formDataToSend.append('gradient_from', formData.gradient_from);
    formDataToSend.append('gradient_to', formData.gradient_to);
    formDataToSend.append('emoji', formData.emoji);
    
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    try {
      setLoading(true);
      
      if (editingBanner) {
        // Update existing banner
        await axios.put(`${API}/banners/${editingBanner.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Banner berhasil diupdate');
      } else {
        // Create new banner
        await axios.post(`${API}/banners`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Banner berhasil ditambahkan');
      }
      
      fetchBanners();
      resetForm();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error(editingBanner ? 'Gagal update banner' : 'Gagal menambahkan banner');
    } finally {
      setLoading(false);
    }
  };

  // Delete banner
  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Yakin ingin menghapus banner ini?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/banners/${bannerId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Banner berhasil dihapus');
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Gagal menghapus banner');
    }
  };

  // Toggle banner active status
  const toggleBannerStatus = async (banner) => {
    const token = localStorage.getItem('admin_token');
    const formDataToSend = new FormData();
    formDataToSend.append('is_active', !banner.is_active);
    
    try {
      await axios.put(`${API}/banners/${banner.id}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Banner ${!banner.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
      toast.error('Gagal mengubah status banner');
    }
  };

  // Edit banner
  const editBanner = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      subtitle: banner.subtitle || '',
      link_url: banner.link_url || '',
      display_order: banner.display_order,
      gradient_from: banner.gradient_from,
      gradient_to: banner.gradient_to,
      emoji: banner.emoji,
      image: null
    });
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      link_url: '',
      display_order: 0,
      gradient_from: 'orange-400',
      gradient_to: 'orange-500',
      emoji: '🎉',
      image: null
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Kelola Banner</h1>
                <p className="text-sm text-muted-foreground">Upload dan atur banner promo</p>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              <span>{showForm ? 'Batal' : 'Tambah Banner'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Form Add/Edit Banner */}
        {showForm && (
          <section className="bg-surface rounded-xl border border-border p-6 animate-slide-up">
            <h2 className="text-xl font-bold text-foreground mb-4">
              {editingBanner ? 'Edit Banner' : 'Tambah Banner Baru'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Judul Banner *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Contoh: Gratis Ongkir 2km"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    required
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                    placeholder="Contoh: Min. belanja 25k"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                {/* Link URL */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Link URL (opsional)
                  </label>
                  <input
                    type="url"
                    value={formData.link_url}
                    onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                    placeholder="https://example.com/promo"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                {/* Display Order */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Urutan Tampil
                  </label>
                  <input
                    type="number"
                    value={formData.display_order || 0}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    min="0"
                  />
                </div>

                {/* Emoji */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Emoji
                  </label>
                  <input
                    type="text"
                    value={formData.emoji}
                    onChange={(e) => setFormData({...formData, emoji: e.target.value})}
                    placeholder="🎉"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    maxLength="2"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {editingBanner ? 'Gambar Banner (kosongkan jika tidak ingin ubah)' : 'Gambar Banner *'}
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <Upload className="w-5 h-5" />
                    <span>Pilih Gambar</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({...formData, image: e.target.files[0]})}
                      className="hidden"
                      required={!editingBanner}
                    />
                  </label>
                  {formData.image && (
                    <span className="text-sm text-muted-foreground">{formData.image.name}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Rekomendasi: 1200x400px, max 2MB, format JPG/PNG
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Menyimpan...' : editingBanner ? 'Update Banner' : 'Simpan Banner'}</span>
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Batal
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Banner List */}
        <section className="bg-surface rounded-xl border border-border p-6">
          <h2 className="text-xl font-bold text-foreground mb-4">
            Daftar Banner ({banners.length})
          </h2>

          {loading && banners.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto"></div>
              <p className="text-muted-foreground mt-3">Memuat banner...</p>
            </div>
          ) : banners.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">📸</div>
              <p className="text-muted-foreground">Belum ada banner</p>
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map((banner) => (
                <div
                  key={banner.id}
                  className="flex items-center gap-4 p-4 border border-border rounded-lg hover:border-primary/40 transition-colors"
                >
                  {/* Banner Preview */}
                  <div className="w-32 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    {banner.image_url ? (
                      <img 
                        src={banner.image_url} 
                        alt={banner.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">
                        {banner.emoji}
                      </div>
                    )}
                  </div>

                  {/* Banner Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{banner.title}</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                        #{banner.display_order}
                      </span>
                      {banner.is_active ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                          Aktif
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          Nonaktif
                        </span>
                      )}
                    </div>
                    {banner.subtitle && (
                      <p className="text-sm text-muted-foreground mb-1">{banner.subtitle}</p>
                    )}
                    {banner.link_url && (
                      <a 
                        href={banner.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        {banner.link_url}
                      </a>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleBannerStatus(banner)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title={banner.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                    >
                      {banner.is_active ? (
                        <Eye className="w-5 h-5 text-success" />
                      ) : (
                        <EyeOff className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                    <button
                      onClick={() => editBanner(banner)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <Edit2 className="w-5 h-5 text-primary" />
                    </button>
                    <button
                      onClick={() => deleteBanner(banner.id)}
                      className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5 text-error" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default BannerManagement;